import type { ReactNode } from "react";

export type HeroStatus = "critical" | "warning" | "ok";

const STATUS: Record<HeroStatus, { dot: string; label: string }> = {
  critical: { dot: "bg-red-400", label: "Requer ação" },
  warning:  { dot: "bg-amber-300", label: "Pede atenção" },
  ok:       { dot: "bg-tm-300", label: "Sob controle" },
};

/**
 * Abertura das telas de Sistema no mesmo desenho da Visão Executiva
 * (ExecutiveHero): a manchete com o número que importa, uma linha de contexto e
 * o selo de status que leva a "O que pede ação". Sem a rota do período, que só
 * faz sentido para faturamento.
 */
export function SystemHero({ headline, detail, status, statusText, loading }: {
  /** frase com o número em destaque (use <HeroNumber>) */
  headline: ReactNode;
  detail?: ReactNode;
  status: HeroStatus;
  /** complemento do selo, ex.: "2 pontos de atenção" */
  statusText?: string;
  loading?: boolean;
}) {
  const st = STATUS[status];
  return (
    <section aria-labelledby="hero-sistema" className="rounded-2xl bg-tm-800 text-white overflow-hidden">
      <div className="px-6 py-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="max-w-3xl">
            <h2 id="hero-sistema" className="text-base text-tm-100 leading-snug">
              {loading ? <span className="inline-block h-6 w-72 rounded bg-white/10 animate-pulse align-middle" /> : headline}
            </h2>
            {!loading && detail && <p className="text-sm text-tm-100 mt-1.5">{detail}</p>}
          </div>
          {!loading && (
            <a
              href="#o-que-pede-acao"
              className="flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 px-4 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <span className={`w-2.5 h-2.5 rounded-full ${st.dot}`} aria-hidden="true" />
              {st.label}
              {statusText && <span className="text-tm-100 font-normal">{statusText}</span>}
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

/** O número da manchete, no tamanho e fonte do faturamento da Executiva. */
export function HeroNumber({ children }: { children: ReactNode }) {
  return <span className="font-display font-semibold text-2xl text-white tabular-nums align-baseline">{children}</span>;
}
